/**
 * useOrgTeam — list of every active person in the caller's org with
 * the full set of role grants each one holds.
 *
 * Drives the read-only roster on /app/team. RLS scopes profiles to
 * the caller's org so that query needs no explicit org filter. The
 * profile_roles query is filtered by the caller's active org_id
 * (from the JWT) so contractor_admin / super_admin viewers see every
 * grant in this org via the admin branch of the profile_roles RLS
 * policy (20260518000003).
 *
 * `roles: string[]` is the multi-role spine fact (M1, 2026-05-18).
 * `role: string` is the legacy primary role from profiles.role,
 * retained as a fallback for tenants that haven't backfilled
 * profile_roles yet.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'
import { useAuthStore }                   from '@/store/authStore'

export interface TeamMemberRow {
  id:         string
  full_name:  string
  email:      string
  role:       string
  roles:      string[]
  created_at: string
}

const ONE_MIN = 60_000

export function useOrgTeam(): UseQueryResult<TeamMemberRow[]> {
  const supabase = useSupabase()
  const orgId    = useAuthStore((s) => {
    const meta = s.user?.app_metadata as { org_id?: string } | undefined
    return meta?.org_id ?? null
  })

  return useQuery({
    queryKey:  ['org-team', orgId ?? null],
    enabled:   !!orgId,
    staleTime: ONE_MIN,
    queryFn: async (): Promise<TeamMemberRow[]> => {
      if (!orgId) return []

      const [profilesRes, rolesRes] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, full_name, email, role, created_at')
          .eq('is_active', true)
          .order('role',      { ascending: true })
          .order('full_name', { ascending: true })
          .limit(500),
        supabase
          .from('profile_roles')
          .select('user_id, role')
          .eq('organization_id', orgId)
          .limit(2000),
      ])
      if (profilesRes.error) throw profilesRes.error
      if (rolesRes.error)    throw rolesRes.error

      const profiles = (profilesRes.data ?? []) as Array<{
        id:         string
        full_name:  string
        email:      string
        role:       string
        created_at: string
      }>
      const grants = (rolesRes.data ?? []) as Array<{ user_id: string; role: string }>

      const byUser = new Map<string, string[]>()
      for (const g of grants) {
        const list = byUser.get(g.user_id) ?? []
        if (!list.includes(g.role)) list.push(g.role)
        byUser.set(g.user_id, list)
      }

      return profiles.map((p): TeamMemberRow => ({
        id:         p.id,
        full_name:  p.full_name,
        email:      p.email,
        role:       p.role,
        roles:      byUser.get(p.id) ?? [p.role],
        created_at: p.created_at,
      }))
    },
  })
}
